import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../styles/theme';
import { supabase, type ProductPublic } from '../lib/supabase';
import { thumbUrl } from '../lib/imageUrl';
import { productModel } from '../lib/productModel';
import { trackEvent } from '../lib/tracking';

type SearchResult = ProductPublic & {
  // Si fue resuelto via variant, traemos info del variant
  variant_sku?: string;
  variant_key?: string;
  variant_finish?: string;
};

export function SearchAutocomplete() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      // Buscar simultáneamente: modelo/SKU NULED y, para operación interna,
      // el SKU impreso en la etiqueta de origen.
      const [prodRes, varRes] = await Promise.all([
        supabase.from('products_public')
          .select('id, sku, brand_code, name, msrp_mxn, msrp_usd, images, lutron_compatible, status, unit, specs, custom_fields')
          .or(`name.ilike.%${q}%,sku.ilike.%${q}%,specs->>modelo_proveedor.ilike.%${q}%`)
          .eq('status', 'active').limit(6),
        supabase.from('product_variants')
          .select(`
            id, full_sku, configuration_key, finish, primary_image_url, product_id, attributes,
            product:products!inner ( id, sku, brand_code, name, msrp_mxn, msrp_usd, images, lutron_compatible, status, unit, specs, custom_fields )
          `)
          .or(`full_sku.ilike.%${q}%,configuration_key.ilike.%${q}%,attributes->>sku_etiqueta.ilike.%${q}%`)
          .limit(6),
      ]);
      const products = (prodRes.data as ProductPublic[]) ?? [];
      const variants = (varRes.data as any[]) ?? [];
      // Mezclar: si una variante apunta a un producto ya en products, no duplicar
      const productIds = new Set(products.map(p => p.id));
      const merged: SearchResult[] = [...products];
      for (const v of variants) {
        if (!v.product || v.product.status !== 'active') continue;
        if (productIds.has(v.product.id)) continue;  // ya está
        merged.push({
          ...v.product,
          images: v.primary_image_url
            ? [{ url: v.primary_image_url, is_thumbnail: true, sort_order: 0 }]
            : v.product.images,
          variant_sku: v.full_sku,
          variant_key: v.configuration_key ?? v.full_sku,
          variant_finish: v.finish,
        });
      }
      setResults(merged.slice(0, 10));
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (q.trim()) {
      void trackEvent('search', { q: q.trim(), source: 'header' });
      navigate(`/catalogo?q=${encodeURIComponent(q.trim())}`);
      setOpen(false);
    }
  };

  return (
    <form ref={ref} onSubmit={submit} style={{ position: 'relative', flex: 1, maxWidth: 540, margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex' }}>
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar productos, modelos, códigos y categorías…"
          aria-label="Buscar"
          style={{
            flex: 1,
            padding: '12px 16px',
            border: 'none',
            borderRadius: 0,
            fontSize: 14,
            fontFamily: theme.font.sans,
            outline: 'none',
            color: theme.text,
          }}
        />
        <button type="submit" aria-label="Buscar" style={{
          background: theme.accent,
          border: 'none',
          padding: '0 18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.text} strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {open && q.trim().length >= 2 && (
        <div style={{
          position: 'absolute',
          left: 0, right: 0, top: '100%',
          background: theme.bg,
          color: theme.text,
          boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
          maxHeight: 460,
          overflowY: 'auto',
          zIndex: 60,
          fontFamily: theme.font.sans,
        }}>
          {loading && (
            <div style={{ padding: 16, fontSize: 13, color: theme.textMuted }}>
              Buscando…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div style={{ padding: 16, fontSize: 13, color: theme.textMuted }}>
              Sin resultados para "<strong>{q}</strong>". Pulsa <kbd style={kbdStyle}>Enter</kbd> para ver el catálogo completo.
            </div>
          )}
          {results.map(r => {
            const img = r.images?.find(i => i.is_thumbnail) ?? r.images?.[0];
            const targetPath = r.variant_key
              ? `/producto/${encodeURIComponent(r.sku)}?v=${encodeURIComponent(r.variant_key)}`
              : `/producto/${encodeURIComponent(r.sku)}`;
            return (
              <a
                key={r.id + (r.variant_key ?? '')}
                href={targetPath}
                onClick={e => {
                  e.preventDefault();
                  void trackEvent('search', { q: q.trim(), source: 'autocomplete', picked_sku: r.variant_sku ?? r.sku });
                  navigate(targetPath);
                  setOpen(false);
                  setQ('');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  borderBottom: `1px solid ${theme.border}`,
                  textDecoration: 'none',
                  color: theme.text,
                }}
              >
                <div style={{
                  width: 44, height: 44,
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  flexShrink: 0,
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  {img && (
                    <img
                      src={thumbUrl(img.url, 120)}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      style={{
                        position: 'absolute', top: 0, left: 0,
                        width: '100%', height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, color: theme.textMuted, fontFamily: theme.font.mono, textTransform: 'uppercase' }}>
                    {r.variant_sku ?? `Modelo: ${productModel(r)}`}
                  </div>
                  <div style={{ fontSize: 13, color: theme.brand, fontWeight: 500, lineHeight: 1.3 }}>
                    {r.name}{r.variant_finish ? ` · ${r.variant_finish}` : ''}
                  </div>
                </div>
                {r.lutron_compatible && (
                  <span style={{
                    fontSize: 9,
                    color: theme.brandTag.lutron,
                    border: `1px solid ${theme.brandTag.lutron}`,
                    padding: '2px 6px',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}>
                    Lutron
                  </span>
                )}
              </a>
            );
          })}
          {!loading && results.length > 0 && (
            <button
              onClick={submit as unknown as React.MouseEventHandler}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: theme.bgFooter,
                color: theme.brand,
                border: 'none',
                borderTop: `1px solid ${theme.border}`,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontFamily: theme.font.sans,
              }}
            >
              Ver todos los resultados →
            </button>
          )}
        </div>
      )}
    </form>
  );
}

const kbdStyle: React.CSSProperties = {
  border: `1px solid ${theme.border}`,
  background: theme.bgSoft,
  padding: '1px 6px',
  fontFamily: theme.font.mono,
  fontSize: 11,
};
